import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Printer,
  Mail,
  Phone,
  MessageCircle,
} from "lucide-react";

const Support = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");

  const faqs = [
    {
      id: 1,
      category: "general",
      question: "What is Trade Bridge and who is it for?",
      answer:
        "Trade Bridge is a premium B2B wholesale marketplace connecting global buyers with trusted FMCGs suppliers, primarily based in Egypt and other international markets. It is designed exclusively for importers and looking for bulk sourcing solutions.",
    },
    {
      id: 2,
      category: "general",
      question: "Do I need to be a registered company to use Trade Bridge?",
      answer:
        "Yes. Trade Bridge is a B2B platform. You must register as a business entity or an authorized representative with valid commercial registration or tax ID.",
    },
    {
      id: 3,
      category: "orders",
      question: "Can I mix products from different suppliers in one container?",
      answer:
        "Yes! consolidation and/or LCLs, We specialize in mixed container consolidation (LCL), allowing you to combine products from multiple suppliers to optimize shipping costs.",
    },
    {
      id: 4,
      category: "shipping",
      question: "What shipping methods do you support?",
      answer:
        "We offer Mixed Containers, LCL, FCL, and Air Freight. Default shipping terms are FOB. Real-time tracking is provided for all shipments.",
    },
    {
      id: 5,
      category: "shipping",
      question: "Who is responsible for import duties and customs clearance?",
      answer:
        "The buyer (consignee) is responsible for all import duties, taxes, and customs clearance in their destination country.",
    },
    {
      id: 6,
      category: "account",
      question: "Is my business information secure?",
      answer:
        "Yes. We take data security seriously and comply with applicable data protection regulations. Your information is never sold to third parties.",
    },
  ];

  const filteredFAQs =
    activeCategory === "all"
      ? faqs
      : faqs.filter((faq) => faq.category === activeCategory);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const categories = [
    { id: "all", label: "All Questions" },
    { id: "general", label: "General" },
    { id: "orders", label: "Orders" },
    { id: "shipping", label: "Shipping & Logistics" },
    { id: "account", label: "Account" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border-t border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[30%] -left-[15%] w-[70%] h-[70%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] lg:min-h-[75vh] flex items-center justify-center pt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 mb-6">
                <HelpCircle className="w-4 h-4 text-primary-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 tracking-wide">
                  SUPPORT CENTER
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                <span className="text-gray-900 dark:text-white">
                  How can we
                </span>{" "}
                <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  help you?
                </span>
              </h1>

              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10">
                Find answers to common questions or contact our support team for
                personalized assistance.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:admin@tradebridge-export.com"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-medium rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
                >
                  <Mail className="w-4 h-4" />
                  Email Support
                </a>
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl text-gray-700 dark:text-gray-300 font-medium rounded-2xl border border-white/50 dark:border-gray-800/50 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-all"
                >
                  <Printer className="w-4 h-4" />
                  Print FAQs
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar Navigation */}
          <aside className="lg:w-80 shrink-0">
            <div className="sticky top-24 space-y-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 px-4">
                CATEGORIES
              </p>

              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 rounded-2xl text-sm font-medium transition-all ${
                    activeCategory === cat.id
                      ? "bg-white dark:bg-gray-800 shadow-md text-primary-600 dark:text-primary-400"
                      : "text-gray-600 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-gray-900/60"
                  }`}
                >
                  <span>{cat.label}</span>
                  {activeCategory === cat.id && (
                    <ChevronRight size={18} className="rotate-90" />
                  )}
                </button>
              ))}

              {/* Quick Contact Card */}
              <div className="mt-10 p-6 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl text-white shadow-xl">
                <MessageCircle className="w-8 h-8 mb-3 opacity-90" />
                <h4 className="font-semibold mb-1">Still need help?</h4>
                <p className="text-sm text-white/80 mb-4">
                  Our support team is available Monday to Friday, 9 AM – 6 PM
                  (Egypt Time).
                </p>
                <div className="space-y-3 text-sm">
                  <a
                    href="mailto:admin@tradebridge-export.com"
                    className="flex items-center gap-2 hover:underline"
                  >
                    <Mail size={16} /> admin@tradebridge-export.com
                  </a>
                  <a
                    href="tel:+201114779577"
                    className="flex items-center gap-2 hover:underline"
                  >
                    <Phone size={16} /> +20 111 477 9577
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* Main FAQs Content */}
          <main className="flex-1 max-w-3xl">
            <div className="space-y-6">
              <AnimatePresence>
                {filteredFAQs.map((faq) => (
                  <motion.div
                    key={faq.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-3xl border border-white/30 dark:border-gray-800/30 shadow-xl overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFAQ(faq.id)}
                      className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-white/20 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <span className="text-lg font-medium text-gray-900 dark:text-white pr-8">
                        {faq.question}
                      </span>
                      <div
                        className={`transition-transform ${openFAQ === faq.id ? "rotate-180" : ""}`}
                      >
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      </div>
                    </button>

                    <AnimatePresence>
                      {openFAQ === faq.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-8 pb-8 text-gray-700 dark:text-gray-300 leading-relaxed border-t border-white/30 dark:border-gray-800/30 pt-6">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* CTA Section */}
            <div className="mt-20 p-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl text-white text-center">
              <h3 className="text-2xl font-bold mb-3">
                Can't find what you're looking for?
              </h3>
              <p className="text-white/80 mb-8 max-w-md mx-auto">
                Our dedicated support team is ready to assist you with any
                inquiry.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-primary-500 font-semibold px-8 py-3 rounded-2xl hover:shadow-xl transition-all"
              >
                Contact Support Now
              </Link>
            </div>

            {/* Footer Note */}
            <div className="mt-16 text-center text-sm text-gray-500 dark:text-gray-400">
              Trade Bridge • Alexandria, Egypt • admin@tradebridge-export.com
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Support;
